import { Component, OnInit, Input, EventEmitter, Output, OnDestroy, OnChanges, SimpleChanges, inject } from '@angular/core';
import {
  AsyncValidatorFn,
  ControlContainer,
  FormControl,
  FormGroup,
  FormGroupDirective,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { ErrorStateOnTouchedOrInvalidValue } from '@app/material/ErrorStateOnTouchedOrInvalidValue';

import { Regex } from '@shared/enums/regex';
import { IconNames } from '@shared/enums/icon-names';

import { MbbDeliveryOptions } from '../../../models/mbb-delivery-options.model';
import { MbbRouter } from '../../../models/mbb-offerings-response.model';
import { MbbProduct } from '../../../models/mbb-product.model';

import { SimCardDelivery, SimCardType } from '@sales/models/simcard.model';
import { FormStatus } from '@sales/models/form-status.enum';
import { SubscriptionHelperService } from '@sales/services/subscription-helper.service';
import { SimService } from '@sales/services/sim.service';
import { SimCardValidator } from '@sales/validators/async-sim-card-validator';
import { WarehouseStateModel } from '@sales/states/warehouse/warehouse.state';
import { BindingType } from '@shared/models/subscription-offering.model';
import { Store } from '@ngxs/store';
import { RepeatedMbbIccValidator } from '@sales/validators/repeated-mbb-icc-validator';
import { FormValidationService } from '@app/shared/services/form-validation.service';
import { AsyncPipe } from '@angular/common';
import { MbbRouterDetailsComponent } from './mbb-router-details/mbb-router-details.component';
import { MbbRouterComponent } from './mbb-router/mbb-router.component';
import { IceRadioButtonComponent } from '@app/shared/components/atoms/ice-radio-button/ice-radio-button.component';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { IceInputFieldComponent } from '@app/shared/components/molecules/ice-input-field/ice-input-field.component';
import { IceSelectBoxComponent } from '@app/shared/components/molecules/ice-select-box/ice-select-box.component';
import { MbbProductHelperService } from '@app/sales/services/mbb-product-helper.service';
import { getCorrelatedBundle } from '@app/core/utils';

@Component({
  selector: 'app-mbb-select-router',
  templateUrl: './mbb-select-router.component.html',
  styleUrls: ['./mbb-select-router.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IceSelectBoxComponent,
    IceInputFieldComponent,
    SpinnerComponent,
    IceRadioButtonComponent,
    MbbRouterComponent,
    MbbRouterDetailsComponent,
    AsyncPipe,
  ],
})
export class MbbSelectRouterComponent implements OnInit, OnChanges, OnDestroy {
  private formGroupDirective = inject(FormGroupDirective);
  private simService = inject(SimService);
  private store = inject(Store);

  MbbDeliveryOptions = MbbDeliveryOptions;
  IconNames = IconNames;
  FormStatus = FormStatus;
  SimCardType = SimCardType;
  SimCardDelivery = SimCardDelivery;
  getSimCardErrorTexts = FormValidationService.getSimCardErrorTexts;

  @Input() mbbProduct: MbbProduct;
  @Input() mbbRouters: MbbRouter[];
  @Input() canSellHardwareWithLogisticsClaim: boolean;
  @Input() canSellHardwareInPhysicalStore: boolean;
  @Input() canSellMBBSubscriptionOnlyClaim: boolean;
  @Input() allowSubscriptionOnlyWithBinding: boolean;
  @Input() warehouseState: WarehouseStateModel;

  @Output() mbbDeliveryChange: EventEmitter<MbbDeliveryOptions> = new EventEmitter();
  @Output() mbbRouterChange: EventEmitter<MbbRouter> = new EventEmitter();
  @Output() mbbRouterDetailsImeiChanged: EventEmitter<string> = new EventEmitter();
  @Output() mbbRouterDetailsIccChanged: EventEmitter<string> = new EventEmitter();
  @Output() orderSimChange: EventEmitter<boolean> = new EventEmitter();
  @Output() isInvalid: EventEmitter<boolean> = new EventEmitter();

  form: FormGroup;

  invalidValueOrTouched = new ErrorStateOnTouchedOrInvalidValue();
  mbbRouterIsInvalid: boolean = false;
  mbbRouterDetailsAreInInvalid: boolean = false;

  subscriptions$: Subscription[] = [];

  public static buildMbbDeliveryDetailsForm(
    productId: string,
    deliveryOption: MbbDeliveryOptions,
    orderSim: boolean,
    icc: string,
    iccValidator: AsyncValidatorFn,
    store: Store
  ): FormGroup {
    const formGroup = new FormGroup({
      deliverySelection: new FormControl(deliveryOption, Validators.required),
      simDelivery: new FormControl(SubscriptionHelperService.getSimCardDelivery(icc, orderSim), Validators.required),
      icc: new FormControl(
        {
          value: icc,
          disabled: true,
        },
        [
          Validators.required,
          Validators.pattern(Regex.ICC),
          Validators.minLength(19),
          Validators.maxLength(20),
          RepeatedMbbIccValidator(productId, store),
        ],
        [iccValidator]
      ),
    });
    MbbSelectRouterComponent.setFormValues(formGroup.controls.deliverySelection.value, null, null, formGroup);
    return formGroup;
  }

  public static setFormValues(
    selectedDeliverySelection: MbbDeliveryOptions,
    mbbRouterDetailsForm: FormGroup,
    mbbChooseMbbRouterForm: FormGroup,
    mbbDeliveryDetailsForm: FormGroup
  ) {
    switch (selectedDeliverySelection) {
      case MbbDeliveryOptions.SendRouterInPost:
        if (mbbRouterDetailsForm) {
          mbbRouterDetailsForm.get('icc').setValue(null);
          mbbRouterDetailsForm.get('icc').disable();
          mbbRouterDetailsForm.get('imei').setValue(null);
          mbbRouterDetailsForm.get('imei').disable();
        }
        if (mbbChooseMbbRouterForm) {
          mbbChooseMbbRouterForm.get('mbbRouter').enable();
        }
        mbbDeliveryDetailsForm.get('icc').setValue(null);
        mbbDeliveryDetailsForm.get('icc').disable();
        mbbDeliveryDetailsForm.get('simDelivery').reset();
        mbbDeliveryDetailsForm.get('simDelivery').disable();

        break;
      case MbbDeliveryOptions.SellInStore:
        if (mbbRouterDetailsForm) {
          mbbRouterDetailsForm.get('icc').enable();
          mbbRouterDetailsForm.get('imei').enable();
        }

        if (mbbChooseMbbRouterForm) {
          mbbChooseMbbRouterForm.get('mbbRouter').enable();
        }

        mbbDeliveryDetailsForm.get('icc').setValue(null);
        mbbDeliveryDetailsForm.get('icc').disable();
        mbbDeliveryDetailsForm.get('simDelivery').reset();
        mbbDeliveryDetailsForm.get('simDelivery').disable();

        break;
      case MbbDeliveryOptions.AlreadyOwnRouter:
        if (mbbRouterDetailsForm) {
          mbbRouterDetailsForm.get('icc').setValue(null);
          mbbRouterDetailsForm.get('icc').disable();
          mbbRouterDetailsForm.get('imei').setValue(null);
          mbbRouterDetailsForm.get('imei').disable();
        }
        if (mbbChooseMbbRouterForm) {
          mbbChooseMbbRouterForm.get('mbbRouter').setValue(null);
          mbbChooseMbbRouterForm.get('mbbRouter').disable();
        }
        mbbDeliveryDetailsForm.get('simDelivery').enable();
        if (mbbDeliveryDetailsForm.get('simDelivery').value === SimCardDelivery.InStore) {
          mbbDeliveryDetailsForm.get('icc').enable();
        } else {
          mbbDeliveryDetailsForm.get('icc').setValue(null);
          mbbDeliveryDetailsForm.get('icc').disable();
        }
        break;
    }
  }

  ngOnInit() {
    this.form = this.buildForm(this.mbbProduct);

    this.subscriptions$.push(
      this.form.controls.deliverySelection.valueChanges
        .pipe(
          tap((newDeliverySelection: MbbDeliveryOptions) => {
            if (newDeliverySelection !== this.mbbProduct.deliveryOption) {
              const mbbRouterDetails = this.formGroupDirective.form.controls.mbbRouterDetails as FormGroup;
              const mbbChooseMbbRouter = this.formGroupDirective.form.controls.chooseMbbRouter as FormGroup;

              MbbSelectRouterComponent.setFormValues(newDeliverySelection, mbbRouterDetails, mbbChooseMbbRouter, this.form);
              this.mbbRouterIsInvalid = false;
              this.mbbRouterDetailsAreInInvalid = false;
              this.mbbDeliveryChange.emit(newDeliverySelection);
            }
          })
        )
        .subscribe()
    );

    this.subscriptions$.push(
      this.form.controls.simDelivery.valueChanges
        .pipe(
          tap((simDelivery: SimCardDelivery) => {
            if (simDelivery === SimCardDelivery.InStore) {
              this.form.controls.icc.enable();
            } else {
              this.form.controls.icc.setValue(null);
              this.form.controls.icc.disable();
            }

            this.orderSimChange.emit(simDelivery === SimCardDelivery.ByMail);
          })
        )
        .subscribe()
    );

    this.subscriptions$.push(
      this.form.controls.icc.valueChanges
        .pipe(
          debounceTime(400),
          distinctUntilChanged(),
          tap((icc: string) => this.mbbRouterDetailsIccChanged.emit(icc))
        )
        .subscribe()
    );

    this.subscriptions$.push(this.form.valueChanges.pipe(tap(() => this.isInvalid.emit(this.form.invalid))).subscribe());

    this.isInvalid.emit(this.form.invalid);
  }

  get routersForSubscription(): MbbRouter[] {
    return (this.mbbRouters ?? []).filter(r => getCorrelatedBundle(r, this.mbbProduct.mbbSubscription?.id));
  }

  get routersAvailableForLogistics(): MbbRouter[] {
    return MbbProductHelperService.getAvailableLogisticsRouters(this.routersForSubscription);
  }

  get canSellRouterWithLogistics(): boolean {
    return this.canSellHardwareWithLogisticsClaim && this.routersAvailableForLogistics.length > 0;
  }

  get isSubscriptionOnlyBlockedByBinding(): boolean {
    return (
      !this.allowSubscriptionOnlyWithBinding &&
      !!this.mbbProduct.mbbSubscription?.bindings?.some(binding => binding.bindingType === BindingType.Handset)
    );
  }

  get canSellSubscriptionOnly(): boolean {
    return this.canSellMBBSubscriptionOnlyClaim && !this.isSubscriptionOnlyBlockedByBinding;
  }

  get canSellRouterInStore(): boolean {
    return this.canSellHardwareInPhysicalStore && this.routersForSubscription.length > 0;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.mbbProduct) {
      const newValue = changes.mbbProduct.currentValue;
      const hasSelectedSubscription =
        newValue.mbbSubscription &&
        newValue.mbbSubscription.constructor === Object &&
        Object.keys(newValue.mbbSubscription).length > 0;

      if (hasSelectedSubscription && this.form) {
        const isSubscriptionSaleOnly = this.form.controls.deliverySelection.value === MbbDeliveryOptions.AlreadyOwnRouter;
        const isSellWithLogistics = this.form.controls.deliverySelection.value === MbbDeliveryOptions.SendRouterInPost;
        const isSellInStore = this.form.controls.deliverySelection.value === MbbDeliveryOptions.SellInStore;

        if (!this.canSellSubscriptionOnly && isSubscriptionSaleOnly) {
          this.form.controls.deliverySelection.reset();
          this.isInvalid.emit(this.form.invalid);
        } else if (
          (this.routersAvailableForLogistics.length === 0 && isSellWithLogistics) ||
          (this.routersForSubscription.length === 0 && isSellInStore)
        ) {
          this.form.controls.deliverySelection.reset();
          this.mbbRouterChange.emit(new MbbRouter());
          this.isInvalid.emit(this.form.invalid);
        }
      }
    }
  }

  get noAvailableRoutersForSellInStoreText(): string {
    return this.canSellHardwareInPhysicalStore && this.routersForSubscription.length === 0
      ? 'Ingen tilgjengelige routere for valgt abonnement'
      : '';
  }

  get noAvailableRoutersForSendInPostText(): string {
    return this.canSellHardwareWithLogisticsClaim && this.routersAvailableForLogistics.length === 0
      ? 'Ingen tilgjengelige routere for valgt abonnement'
      : '';
  }

  get iccField() {
    return this.form.controls.icc;
  }

  private buildForm(mbbProduct: MbbProduct): FormGroup {
    const existingFormGroup = this.formGroupDirective.form.get('mbbDeliveryDetails') as FormGroup;
    if (existingFormGroup) {
      this.formGroupDirective.form.removeControl('mbbDeliveryDetails');
    }
    this.formGroupDirective.form.addControl(
      'mbbDeliveryDetails',
      MbbSelectRouterComponent.buildMbbDeliveryDetailsForm(
        mbbProduct.id,
        mbbProduct.deliveryOption,
        existingFormGroup?.controls.simDelivery.value === SimCardDelivery.ByMail,
        mbbProduct.icc,
        SimCardValidator(this.simService, SimCardType.Mbb, () => {
          this.formGroupDirective.form.updateValueAndValidity();
          this.isInvalid.emit(this.formGroupDirective.form.invalid);
        }),
        this.store
      )
    );

    return this.formGroupDirective.form.get('mbbDeliveryDetails') as FormGroup;
  }

  onMbbRouterIsInvalid(value: boolean) {
    this.mbbRouterIsInvalid = value;
    this.isInvalid.emit(this.mbbRouterIsInvalid || this.mbbRouterDetailsAreInInvalid);
  }

  onMbbRouterDetailsAreInvalid(value: boolean) {
    this.mbbRouterDetailsAreInInvalid = value;
    this.isInvalid.emit(this.mbbRouterIsInvalid || this.mbbRouterDetailsAreInInvalid);
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
